/**
 * The driver between the action list and the DOM, shared by graphic.mjs and the editor's static entry point:
 * data versions (one per update, measured once per distinct data), the schedule resolution, frame rendering and
 * pruning of settled history (real time drops it; non-real time freezes it in epochs).
 */
import type { Field, Scene } from '@prism-graphics/scene-schema';
import { carryOver, resolveSchedule, type Bound, type Box, type EngineAction, type FieldBinding, type Initial, type Resolution } from './engine/resolve.ts';
import { evaluate, versionAt, visibleSince } from './engine/evaluate.ts';
import { isColor } from './engine/color.ts';
import type { Data, Renderer, Snapshot } from './dom.ts';

interface Version {
  data: Data;
  snapshot: Snapshot;
  bound: Bound;
}

/** Normalise incoming data to the fields' types; unknown keys and wrongly typed values are ignored. */
export function mergeData(fields: readonly Field[], prev: Data, incoming: unknown, imagePaths: Record<string, string>): Data {
  const next: Data = { ...prev };
  if (typeof incoming !== 'object' || incoming === null) return next;
  const src = incoming as Record<string, unknown>;
  for (const f of fields) {
    if (!Object.prototype.hasOwnProperty.call(src, f.id)) continue;
    const v = src[f.id];
    switch (f.type) {
      case 'string':
        if (typeof v === 'string') next[f.id] = f.maxLength !== undefined ? [...v].slice(0, f.maxLength).join('') : v;
        else if (typeof v === 'number' && Number.isFinite(v)) next[f.id] = String(v);
        break;
      case 'number': {
        const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
        if (Number.isFinite(n)) next[f.id] = Math.min(f.maximum ?? Infinity, Math.max(f.minimum ?? -Infinity, f.integer ? Math.round(n) : n));
        break;
      }
      case 'boolean':
        if (typeof v === 'boolean') next[f.id] = v;
        break;
      case 'color':
        if (isColor(v)) next[f.id] = v;
        break;
      case 'image-choice':
        if (typeof v === 'string' && f.choices.some((h) => imagePaths[h] === v || h === v)) next[f.id] = imagePaths[v] ?? v;
        break;
    }
  }
  return next;
}

export function defaultData(fields: readonly Field[], assetPaths: Record<string, string>): Data {
  const d: Data = {};
  for (const f of fields) d[f.id] = f.type === 'image-choice' ? (assetPaths[f.default] ?? f.default) : f.default;
  return d;
}

/** Measured layouts kept for reuse (LRU). Versions hold their own snapshot, so an evicted one is only re-measured. */
const SNAPSHOT_CACHE = 64;

/**
 * A stretch of history resolved on its own: valid for t >= `from` (and, once a later epoch exists, before its
 * `from`). The first starts from the load data; later ones from the state pruning carried over at `from`.
 */
interface Epoch {
  from: number;
  base: Data;
  initial: Initial | undefined;
  actions: EngineAction[];
  versions: Version[];
  res: Resolution | undefined;
}

export class Driver {
  readonly r: Renderer;
  readonly #scene: Scene;
  readonly #assetPaths: Record<string, string>;
  readonly #bindings: Map<string, FieldBinding[]>;
  readonly #snapshots = new Map<string, Snapshot>();
  /** The version whose data and layout the DOM shows (any change of object re-applies). */
  #applied: Version | undefined;
  /** The epoch new actions go to. */
  #live: Epoch;
  /** Non-real time: earlier epochs, by `from` (random access still renders the frames before the live one). */
  #frozen: Epoch[] = [];
  /** Real time: videos play; otherwise they are seeked to each frame. */
  realtime = false;

  constructor(r: Renderer, scene: Scene, assetPaths: Record<string, string>, base: Data, measured?: Snapshot) {
    this.r = r;
    this.#scene = scene;
    this.#assetPaths = assetPaths;
    this.#live = { from: -Infinity, base, initial: undefined, actions: [], versions: [], res: undefined };
    this.#bindings = new Map(scene.fields.map((f) => [f.id, f.bindings]));
    if (measured) this.#snapshots.set(this.#key(base), measured);
  }

  /** The live epoch's resolution (its actions are the ones last passed to update()). */
  get res(): Resolution | undefined {
    return this.#live.res;
  }

  /** Where the live epoch starts: actions before it must go through reopen() first. */
  get from(): number {
    return this.#live.from;
  }

  /** Recompute data versions (measuring new ones) and the schedule resolution of the live epoch for `actions`. */
  update(actions: EngineAction[]): void {
    const e = this.#live;
    const fields = this.#scene.fields;
    const sorted = [...actions].sort((a, b) => a.t - b.t || a.seq - b.seq);
    const versions: Version[] = [this.#version(e.base)];
    for (const a of sorted) {
      if (a.type !== 'updateAction') continue;
      const prev = versions[versions.length - 1]!;
      const data = mergeData(fields, prev.data, a.data, this.#assetPaths);
      a.version = versions.length;
      a.changedFields = fields.filter((f) => prev.data[f.id] !== data[f.id]).map((f) => f.id);
      versions.push(this.#version(data));
    }
    e.actions = actions;
    e.versions = versions;
    e.res = resolveSchedule({
      timeline: this.#scene.timeline,
      stepCount: this.#scene.timeline.steps.length,
      actions,
      boxes: (i): Record<string, Box> => versions[i]?.snapshot.boxes ?? {},
      bound: (i) => versions[i]?.bound ?? {},
      bindings: this.#bindings,
      ...(e.initial ? { initial: e.initial } : {}),
    });
  }

  #version(data: Data): Version {
    const key = this.#key(data);
    const cache = this.#snapshots;
    let snapshot = cache.get(key);
    if (snapshot) {
      // Most recently used last.
      cache.delete(key);
    } else {
      this.r.applyData(data);
      snapshot = this.r.measure();
      // The DOM now shows this data: the next render re-applies the version it needs.
      this.#applied = undefined;
      if (cache.size >= SNAPSHOT_CACHE) cache.delete(cache.keys().next().value!);
    }
    cache.set(key, snapshot);
    return { data, snapshot, bound: this.r.bound(data) };
  }

  #key(data: Data): string {
    return JSON.stringify(this.#scene.fields.map((f) => data[f.id]));
  }

  /** The epoch that decides the frame at t. */
  #epochAt(t: number): Epoch {
    const fz = this.#frozen;
    if (fz.length === 0 || t >= this.#live.from) return this.#live;
    // The last frozen epoch starting at or before t (the first starts at -Infinity).
    let lo = 0;
    let hi = fz.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (fz[mid]!.from <= t) lo = mid;
      else hi = mid - 1;
    }
    return fz[lo]!;
  }

  /** Evaluate and write the frame at t. */
  render(t: number, forceVisible = false): void {
    const r = this.r;
    const e = this.#epochAt(t);
    const res = e.res;
    if (!res) return;
    const ver = e.versions[versionAt(res, t)] ?? e.versions[0]!;
    if (ver !== this.#applied) {
      r.applyData(ver.data);
      r.applySnapshot(ver.snapshot);
      r.invalidate();
      this.#applied = ver;
    }
    const frame = evaluate(res, t, r.base, r.count);
    if (forceVisible) frame.visible = true;
    r.write(frame, (i) => e.versions[i]);
    const since = forceVisible ? 0 : visibleSince(res, t);
    r.media(since === undefined ? undefined : t - since, this.realtime);
  }

  /**
   * Close the live epoch at P when nothing but a hold loop changes after it (ADR 0019): the data at P becomes the
   * base and the deciding segments are carried over, so frames at t >= P are unchanged. Real time drops the history
   * (its clock never goes back); non-real time keeps it as a frozen epoch for the frames before P (random access).
   * Returns true when it pruned; the caller then clears its action list.
   */
  prune(P: number): boolean {
    const e = this.#live;
    const res = e.res;
    const init = res && carryOver(res, P, 0);
    if (!init) return false;
    if (!this.realtime) this.#frozen.push(e);
    this.#live = { from: P, base: e.versions[versionAt(res, P)]!.data, initial: init, actions: [], versions: [], res: undefined };
    this.#applied = undefined;
    this.update([]);
    return true;
  }

  /**
   * Non-real time: an action at t before the live epoch's start lands in history a frozen epoch decided. The
   * epochs from the one containing t on become live again (from their carried state); returns their actions and
   * the live ones, to be resolved again with the new action.
   */
  reopen(t: number): EngineAction[] {
    let actions = this.#live.actions;
    while (this.#frozen.length > 0 && t < this.#live.from) {
      this.#live = this.#frozen.pop()!;
      actions = [...this.#live.actions, ...actions];
    }
    this.#applied = undefined;
    return actions;
  }

  /** Back to the load state with no actions; returns every action of every epoch (oldest first). */
  reset(): EngineAction[] {
    const all = [...this.#frozen, this.#live];
    const first = all[0]!;
    this.#frozen = [];
    this.#live = { from: -Infinity, base: first.base, initial: undefined, actions: [], versions: [], res: undefined };
    this.#applied = undefined;
    return all.flatMap((e) => e.actions);
  }
}
